// src/stores/vigStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export enum VIGState {
    ABSENT = 'ABSENT',
    PENDING = 'PENDING',
    CONFIRMED = 'CONFIRMED',
    SWAP_DETECTED = 'SWAP_DETECTED'
}

export interface VehicleData {
    vin?: string;
    make?: string;
    model?: string;
    year?: number;
    engineCode?: string;
    confidence: number;
}

export interface VIGSession {
    sessionId: string;
    state: VIGState;
    vehicleData: VehicleData;
    checksumValid: boolean;
    swapDetected: boolean;
    expiresAt: number;
    lastActivity: number;
}

interface VIGStore {
    session: VIGSession | null;
    isLoading: boolean;
    error: string | null;

    initialize: () => Promise<void>;
    setSession: (session: VIGSession | null) => void;
    updateState: (state: VIGState) => void;
    requireConfirmed: () => boolean;
    reset: () => void;
}

export const useVIGStore = create<VIGStore>()(
    persist(
        (set, get) => ({
            session: null,
            isLoading: false,
            error: null,

            initialize: async () => {
                set({ isLoading: true });
                const current = get().session;
                if (current && current.expiresAt < Date.now()) {
                    set({ session: { ...current, state: VIGState.ABSENT }, isLoading: false });
                } else {
                    set({ isLoading: false });
                }
            },

            setSession: (session) => set({ session }),

            updateState: (state) => set((prev) => ({
                session: prev.session ? { ...prev.session, state } : null
            })),

            requireConfirmed: () => {
                const state = get().session?.state;
                if (state !== VIGState.CONFIRMED) {
                    window.dispatchEvent(new CustomEvent('vig:required', {
                        detail: { state: state || VIGState.ABSENT }
                    }));
                    return false;
                }
                return true;
            },

            reset: () => set({ session: null, error: null })
        }),
        {
            name: 'vig-storage',
        }
    )
);
